// Wave 25.17 — Free-tier mapping stack.
// Shared Leaflet bootstrap used by MapView, MapEmbed, PlaceInput's pin
// picker and TripDetail's route map. Loads tiles from OpenStreetMap
// (default) and ESRI World Imagery (satellite), so we can fully retire
// the Google Maps JavaScript SDK and stop the daily Geocoding bill.
//
// Why a shared module?
//   1. We import Leaflet's CSS once.
//   2. We patch Leaflet's default-icon URL so markers don't 404 in
//      Webpack-bundled builds.
//   3. Every map surface in the app gets the same satellite/streets
//      toggle and pin look-and-feel for free.

import L from "leaflet";
import "leaflet/dist/leaflet.css";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

// Webpack rewrites the default icon URLs to "/static/media/...png", but
// Leaflet hardcodes "marker-icon.png" relative to its CSS URL — fix it.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({ iconUrl, iconRetinaUrl, shadowUrl });

const OSM_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR = '&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>';

const ESRI_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
const ESRI_ATTR = "Imagery © Esri, Maxar, Earthstar Geographics, and the GIS User Community";

/** Build a HidroVerse-branded teal pin (DivIcon, no PNG needed). */
export function tealPin(label = "") {
  const html = `
    <div style="
      transform: translate(-50%, -100%);
      display:flex; flex-direction:column; align-items:center;
    ">
      <div style="
        width:22px; height:22px; border-radius:50%;
        background:#1FB6C1; border:2px solid #0a0b0e;
        box-shadow:0 2px 8px rgba(0,0,0,.4);
        display:flex; align-items:center; justify-content:center;
        color:#0a0b0e; font:700 11px/1 system-ui;
      ">${label || ""}</div>
      <div style="
        width:0; height:0;
        border-left:5px solid transparent;
        border-right:5px solid transparent;
        border-top:8px solid #1FB6C1;
        margin-top:-1px;
      "></div>
    </div>`;
  return L.divIcon({
    className: "hv-pin",
    html,
    iconSize: [22, 30],
    iconAnchor: [11, 30],
  });
}

/**
 * Initialise a Leaflet map with a Streets/Satellite layer toggle.
 * Returns { map, streets, satellite }.
 */
export function createMap(el, { center = [-25.4753, 30.9694], zoom = 6, satelliteFirst = false } = {}) {
  const map = L.map(el, {
    center,
    zoom,
    zoomControl: true,
    attributionControl: true,
    worldCopyJump: true,
  });
  const streets = L.tileLayer(OSM_URL, {
    attribution: OSM_ATTR,
    maxZoom: 19,
    crossOrigin: true,
  });
  const satellite = L.tileLayer(ESRI_URL, {
    attribution: ESRI_ATTR,
    maxZoom: 19,
    crossOrigin: true,
  });
  (satelliteFirst ? satellite : streets).addTo(map);
  L.control.layers(
    { Streets: streets, Satellite: satellite },
    {},
    { position: "topright", collapsed: true },
  ).addTo(map);
  return { map, streets, satellite };
}

export { L };
